import React, { useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Badge,
  IconButton,
  Spinner,
  Center,
  useColorModeValue,
  useToast,
  Select,
  SimpleGrid,
  Avatar,
  Spacer
} from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiShare2, FiBook } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import { useAuth } from '../contexts/AuthContext';
import { useBooks } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';

const Books: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    books,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingBooks,
    loadBooks,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useBooks();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const genres = [
    { id: 'all', name: 'All Genres' },
    ...GENRES.map(genre => ({ id: genre.toLowerCase(), name: genre }))
  ];

  useEffect(() => {
    loadBooks(user?.uid);
  }, [selectedGenre, loadBooks, user?.uid]);

  // Refresh books when page comes into focus (useful after editing)
  useEffect(() => {
    const handleFocus = () => {
      loadBooks(user?.uid);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadBooks, user?.uid]);

  const onGenreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGenre(e.target.value);
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error || books.length === 0) {
    return (
      <Center h="50vh">
        <VStack gap={6} textAlign="center" maxW="400px">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Content Not Found
          </Text>
          <Text fontSize="md" color={labelColor}>
            Content not found or no longer available
          </Text>
          <VStack gap={3}>
            <Button onClick={() => loadBooks(user?.uid)} colorScheme="purple" size="lg">
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/upload')} 
              variant="outline" 
              colorScheme="purple"
            >
              Upload Content
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} w="100%" pt={0} mt={0} mb={0} pb={0}>
      <VStack gap={6} maxW="1400px" mx="auto" w="100%" pt={0} mt={0}>
        {/* Header */}
        <Flex justify="space-between" align="center" w="full">
          <Text fontSize="3xl" fontWeight="bold" color={titleColor}>
            Books & Comics
          </Text>
          <Select
            value={selectedGenre}
            onChange={onGenreChange}
            placeholder="Filter by Genre"
            maxW="200px"
            bg={cardBg}
            color={textColor}
            borderColor={labelColor}
          >
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </Select>
        </Flex>

        {/* Books & Comics Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6} w="full">
          {books.map((book) => (
            <Box
              key={book.id}
              bg={cardBg}
              borderRadius="lg"
              shadow="md"
              overflow="hidden"
              cursor="pointer"
              onClick={() => navigate(`/book/${book.id}`)}
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              transition="all 0.2s"
            >
              {/* Thumbnail */}
              <Box position="relative" h="200px">
                <CachedImage
                  src={
                    (typeof book.assets?.coverFile === 'string' ? book.assets.coverFile : '') ||
                    book.poster || 
                    book.thumbnail
                  }
                  alt={book.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={2}
                  left={2}
                  colorScheme="blue"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {book.category}
                </Badge>
                <IconButton
                  aria-label="View Book"
                  icon={<IconWrapper icon={FiBook} size={24} />}
                  position="absolute"
                  bottom={2}
                  right={2}
                  colorScheme="blue"
                  size="lg"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/book/${book.id}`);
                  }}
                />
              </Box>

              {/* Content Info */}
              <VStack p={4} align="flex-start" spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1}>
                  {book.title}
                </Text>
                <HStack spacing={2} w="full" justify="space-between">
                  <HStack>
                    <Avatar size="xs" src={book.uploaderAvatar} name={book.uploader} />
                    <Text fontSize="sm" color={labelColor}>
                      {book.uploader}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color={labelColor}>
                    {book.views.toLocaleString()} views
                  </Text>
                </HStack>

                {/* Actions */}
                <HStack spacing={2} pt={2} w="full">
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsUp} size={16} />}
                    size="sm"
                    colorScheme={userLikes[book.id] === 'like' ? 'green' : 'gray'}
                    variant={userLikes[book.id] === 'like' ? 'solid' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(book.id, user!.uid);
                    }}
                    isLoading={likingBooks.has(book.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[book.id]?.likes || 0}
                  </Button>
                  <Button
                    leftIcon={<IconWrapper icon={FiThumbsDown} size={16} />}
                    size="sm"
                    colorScheme={userLikes[book.id] === 'dislike' ? 'red' : 'gray'}
                    variant={userLikes[book.id] === 'dislike' ? 'solid' : 'outline'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(book.id, user!.uid);
                    }}
                    isLoading={likingBooks.has(book.id)}
                    isDisabled={!user}
                  >
                    {likeCounts[book.id]?.dislikes || 0}
                  </Button>
                  <Spacer />
                  <IconButton
                    aria-label="Share Book"
                    icon={<IconWrapper icon={FiShare2} size={16} />}
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShare(book.id, user?.uid);
                    }}
                    isDisabled={!user}
                  />
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default Books;
